import { THREE, OrbitControls, MobileDetector, gsap, errorManager, vibrations, CustomEvents } from '@powerplay/common';
import { LightTypes } from './types';
import { RendererInfo, StatsInfo } from '@powerplay/stats-system';
import { LightsManager } from './LightsManager';
import { ShadowsManager } from './ShadowsManager';
import { BatchingManager } from './BatchingManager';
import { RenderManager } from './RenderManager';
import { CorePhases } from '@powerplay/phases-system';
import { CameraTypes } from '@powerplay/camera-system';
import { CannonPhysics, CannonDebugger } from '@powerplay/physics';
import { TreesBatchingGenerator } from '@powerplay/generator-system';
import { SettingsTypes } from '@powerplay/settings';
import { renderOptions } from './config';
import { mergeTexturesFragmentShader, mergeTexturesVertexShader } from './shaders';
export class Game3DManager {
    particleManager;
    timeManager;
    corePhasesManager;
    audioManager;
    settings;
    fpsManager;
    cameraManager;
    inputsManager;
    scene = new THREE.Scene();
    controls;
    physics = new CannonPhysics();
    clock = new THREE.Clock();
    statsInfo;
    rendererInfo;
    animations = new Map();
    lightsManager;
    shadowsManager;
    renderManager = new RenderManager();
    batchingManager = new BatchingManager();
    paused = false;
    pausedCounter = 0;
    dataForCore;
    texturesToUse;
    materialsToUse = {
        basic: new Map()
    };
    defaultMaterialKey;
    meshMaterialMap = new Map();
    isLeftInputs = false;
    physicsEnabled = false;
    envMapsData = [];
    cannonDebugger = undefined;
    isLocalEnv = false;
    lastDeltaAnimations = 0;
    useLastDeltaAnimations = false;
    callbackCustomUpdateQuality;
    firstUpdateDone = false;
    readyFirstUpdate = false;
    prefixMeshesGroupEnabled = false;
    DEBUG_ENABLED = false;
    UPDATE_GAME_STEP_BY_STEP = false;
    PHYSICS_DISABLED = false;
    DEBUG_WIREFRAME_MESH = false;
    SHOW_STATS_INFO = false;
    SHOW_RENDERER_INFO = false;
    FIXED_DELTA = 1;
    constructor(particleManager, timeManager, corePhasesManager, audioManager, settings, fpsManager, cameraManager, inputsManager) {
        this.particleManager = particleManager;
        this.timeManager = timeManager;
        this.corePhasesManager = corePhasesManager;
        this.audioManager = audioManager;
        this.settings = settings;
        this.fpsManager = fpsManager;
        this.cameraManager = cameraManager;
        this.inputsManager = inputsManager;
        this.FIXED_DELTA = 1 / this.fpsManager.fpsLimit;
    }
    setCallbackCustomUpdateQuality(callback) {
        this.callbackCustomUpdateQuality = callback;
    }
    setIsLocalEnv(isLocalEnv) {
        this.isLocalEnv = isLocalEnv;
    }
    create() {
        console.log(`----------- THREEJS verzia ${THREE.REVISION} -----------`);
        this.renderManager.create();
        this.cameraManager.createCameras();
        this.lightsManager = new LightsManager(this.scene);
        this.shadowsManager = new ShadowsManager(this.scene, this.lightsManager, this.renderManager.renderer, this.settings);
    }
    init(callback, data) {
        const { physicsDebug, stepByStep, debugCamera, physicsDisabled, showWireframeMesh, showStatsInfo, showRendererInfo } = data.debugConfig;
        this.DEBUG_ENABLED = physicsDebug;
        this.UPDATE_GAME_STEP_BY_STEP = stepByStep;
        this.PHYSICS_DISABLED = physicsDisabled;
        this.DEBUG_WIREFRAME_MESH = showWireframeMesh;
        this.SHOW_STATS_INFO = showStatsInfo;
        this.SHOW_RENDERER_INFO = showRendererInfo;
        if (debugCamera) {
            this.cameraManager.setCamera(CameraTypes.debug);
            this.controls = new OrbitControls(this.cameraManager.getCamera(CameraTypes.debug), this.renderManager.getDomElement());
        }
        this.statsInfo = new StatsInfo(this.SHOW_STATS_INFO);
        this.rendererInfo = new RendererInfo(this.SHOW_RENDERER_INFO, document.body, this.renderManager.renderer);
        this.dataForCore = data;
        this.shadowsManager.init();
        this.batchingManager.init(data.batching);
        if (this.DEBUG_ENABLED) {
            this.cannonDebugger = CannonDebugger(this.scene, this.physics.getPhysicsWorld.bodies);
        }
        const inputSchema = this.dataForCore.inputSchema ? this.dataForCore.inputSchema : 'default';
        this.inputsManager.init(this.dataForCore.callbacks.inputs, this.updateGame, inputSchema);
        callback();
    }
    getTexture(name) {
        const text = this.texturesToUse.main.get(name);
        if (!text) {
            throw new Error('Nezadali ste spravny nazov');
        }
        return text;
    }
    set3DObjectsAfterLoaded = (objects3DData, texturesData) => {
        console.log(objects3DData);
        objects3DData.forEach((value, key) => {
            console.log(key, JSON.parse(JSON.stringify(value.scene)));
            console.log(key, value.scene);
            this.generateLocalBatchingOutput(key, value);
            const group = value.scene;
            console.log(group);
            if (group) {
                group.name = key;
                this.scene.add(group);
                const animationsData = value.animations;
                if (animationsData)
                    this.animations.set(key, animationsData);
            }
        });
        if (this.DEBUG_WIREFRAME_MESH) {
            this.setWireframeGeometry();
        }
        this.texturesToUse = texturesData;
        this.setCubemap();
        this.createMaterials();
        this.setUp3DObjects();
    };
    setCubemap() {
        if (this.texturesToUse.cubemap) {
            this.texturesToUse.cubemap.mapping = THREE.EquirectangularRefractionMapping;
            this.texturesToUse.cubemap.flipY = true;
            this.scene.background = this.texturesToUse.cubemap;
        }
    }
    generateLocalBatchingOutput = (key, value) => {
        if (this.isLocalEnv) {
            if (key === 'TreesLeft') {
                TreesBatchingGenerator.generate('TreesLeft', value, new THREE.Vector3(110.5, 838.03, -7.72));
            }
        }
    };
    start = () => {
        this.statsInfo.begin();
        this.fpsManager.init();
        this.fpsManager.startCounting();
        requestAnimationFrame(this.update);
    };
    restart = () => {
        this.fpsManager.reset();
        this.fpsManager.startCounting();
    };
    createMaterials() {
        for (const [key, value] of Object.entries(this.dataForCore.materials)) {
            this.createMaterial(key, value);
            value.meshesArray?.forEach((meshName) => {
                this.meshMaterialMap.set(meshName, key);
            });
            if (value.isDefault)
                this.defaultMaterialKey = key;
        }
        if (!this.defaultMaterialKey) {
            throw new Error(errorManager.showBox('No default material!'));
        }
    }
    createMaterial(name, data) {
        const { transparent, alpha, textureName, secondTextureName, color, lightmap, vertexColors, alphamap, envmap, reflectivity, blending, depthWrite, depthTest, opacity } = data;
        const materialData = {
            side: THREE.DoubleSide,
            transparent: transparent ?? false,
            alphaTest: alpha ?? 0,
            vertexColors: vertexColors ?? false,
            blending: blending ?? THREE.NormalBlending,
            depthWrite: depthWrite ?? true,
            depthTest: depthTest ?? true
        };
        let texture;
        if (textureName) {
            const textureMain = this.texturesToUse.main.get(textureName);
            if (!textureMain) {
                console.warn(`Textura ${textureName} nenacitana pre material ${name}`);
                return;
            }
            texture = textureMain;
        }
        if (color)
            materialData.color = color;
        if (opacity !== undefined)
            materialData.opacity = opacity;
        if (lightmap) {
            materialData.lightMap = this.texturesToUse.lightmap.get(lightmap);
        }
        if (alphamap)
            materialData.alphaMap = this.texturesToUse.alphamap.get(alphamap);
        if (envmap) {
            materialData.envMap = this.texturesToUse.envmap.get(envmap);
            this.envMapsData.push({
                mapName: envmap,
                materialName: name
            });
        }
        if (materialData.envMap) {
            materialData.envMap.mapping = THREE.EquirectangularReflectionMapping;
        }
        if (reflectivity !== undefined)
            materialData.reflectivity = reflectivity;
        console.log(`creating material ${name}, shader? `);
        let material;
        if (secondTextureName === undefined) {
            material = new THREE.MeshBasicMaterial({
                ...materialData,
                map: texture
            });
        }
        else {
            const secondTexture = this.texturesToUse.main.get(secondTextureName);
            if (!secondTexture) {
                console.warn(`Druha textura ${secondTextureName} nenacitana pre material ${name}`);
                return;
            }
            material = new THREE.ShaderMaterial({
                uniforms: THREE.UniformsUtils.merge([{
                        texture1: { value: texture },
                        texture2: { value: secondTexture },
                        alpha: { value: 1 }
                    }]),
                vertexShader: mergeTexturesVertexShader,
                fragmentShader: mergeTexturesFragmentShader,
                side: THREE.DoubleSide
            });
        }
        material.name = name;
        this.materialsToUse.basic.set(name, material);
    }
    setUp3DObjects() {
        this.scene.traverse((object) => {
            if (!(object instanceof THREE.Mesh))
                return;
            const mesh = object;
            mesh.matrixAutoUpdate = false;
            mesh.updateMatrix();
            if (this.dataForCore.meshesCastShadows.includes(mesh.name)) {
                mesh.castShadow = true;
                mesh.layers.set(1);
            }
            this.setMaterialToMesh(mesh);
        });
    }
    getMesh(meshName) {
        const mesh = this.scene.getObjectByName(meshName);
        if (!mesh) {
            throw new Error(errorManager.showBox(`Mesh ${meshName} bol undefined`));
        }
        if (!(mesh instanceof THREE.Mesh)) {
            throw new Error(errorManager.showBox(`Mesh ${meshName} nebol THREE.Mesh`));
        }
        return mesh;
    }
    getObject3D(objectName) {
        const object3D = this.scene.getObjectByName(objectName);
        if (!object3D) {
            throw new Error(errorManager.showBox(`Object ${objectName} bol undefined`));
        }
        return object3D;
    }
    getMaterialNameForMesh(meshName) {
        let specificMaterialName = this.meshMaterialMap.get(meshName);
        if (!specificMaterialName)
            specificMaterialName = '';
        const materialName = specificMaterialName || this.defaultMaterialKey;
        if (!this.dataForCore.materials[materialName]) {
            throw new Error(errorManager.showBox(`No material was set on mesh ${meshName}`));
        }
        return materialName;
    }
    setMaterialToMesh(paramMesh) {
        const mesh = paramMesh;
        if (!mesh.name) {
            console.warn('Nevieme dat meshu material, lebo nemal meno', mesh);
            return;
        }
        if (this.prefixMeshesGroupEnabled && mesh.parent && mesh.parent instanceof THREE.Group) {
            mesh.name = `${mesh.parent.name}_${mesh.name}`;
        }
        const materialName = this.getMaterialNameForMesh(mesh.name);
        const { basic } = this.materialsToUse;
        console.log(`set texture name ${materialName} for mesh ${mesh.name}`);
        const material = basic.get(materialName);
        if (!material) {
            throw new Error(errorManager.showBox(`Basic material ${materialName} nebol najdeny`));
        }
        mesh.material = material;
    }
    getRenderOptions() {
        const settingQuality = this.settings.getSetting(SettingsTypes.quality);
        if (renderOptions[settingQuality]) {
            return renderOptions[settingQuality];
        }
        throw new Error(errorManager.showBox(`RenderOptions undefined, value ${settingQuality}`));
    }
    updateQuality = (settingValue) => {
        console.log(`update quality ${settingValue}`);
        const renderOptionsInstance = this.getRenderOptions();
        this.renderManager.setSize(MobileDetector.isMobile() ? renderOptionsInstance[1] : renderOptionsInstance[0]);
        this.shadowsManager.setTextureSize(renderOptionsInstance[2]);
        const shadowsEnabled = this.shadowsManager.isEnabled(settingValue);
        this.shadowsManager.setEnabled(shadowsEnabled);
        this.lightsManager.setEnabled(shadowsEnabled, LightTypes.directionalMain);
        this.batchingManager.setVisibilityForLayers(settingValue);
        this.setMeshesAnisotropy(this.dataForCore.meshesAnisotropy ?? [], renderOptionsInstance[3]);
        this.fpsManager.animationsPerformanceLock30 = settingValue <= 6;
        this.changeParticleMangerState(settingValue);
        this.setEnvMaps(settingValue >= 7);
        this.callbackCustomUpdateQuality?.(settingValue);
    };
    setEnvMaps(enabled) {
        this.envMapsData.forEach((envMapData) => {
            const materialBasic = this.materialsToUse.basic.get(envMapData.materialName);
            const envMap = this.texturesToUse.envmap.get(envMapData.mapName) ?? null;
            if (materialBasic)
                materialBasic.envMap = enabled ? envMap : null;
        });
    }
    changeParticleMangerState(quality) {
        this.particleManager.checkActivateEmitters(quality);
    }
    updateIsLeft = (value) => {
        this.isLeftInputs = false;
        if (value)
            this.isLeftInputs = true;
    };
    updateVibrations = (value) => {
        vibrations.active = value === 1;
    };
    setMeshesAnisotropy(meshes, anisotropy) {
        meshes.forEach((value) => {
            const mesh = this.getMesh(value);
            const material = mesh.material;
            if (material.map) {
                material.map.anisotropy = anisotropy;
                material.map.needsUpdate = true;
            }
            const shaderMaterial = mesh.material;
            if (shaderMaterial.uniforms) {
                const texture1 = shaderMaterial.uniforms.texture1;
                if (texture1) {
                    texture1.value.anisotropy = anisotropy;
                    texture1.value.needsUpdate = true;
                }
                const texture2 = shaderMaterial.uniforms.texture2;
                if (texture2) {
                    texture2.value.anisotropy = anisotropy;
                    texture2.value.needsUpdate = true;
                }
            }
        });
    }
    setMeshRenderOrder(name, order) {
        const mesh = this.getObject3D(name);
        if (mesh)
            mesh.renderOrder = order;
    }
    pauseGame() {
        this.pausedCounter++;
        if (this.pausedCounter > 1)
            return;
        gsap.globalTimeline.pause();
        this.audioManager.pauseAllAudio();
        this.paused = true;
        this.statsInfo?.setFps();
    }
    resumeGame() {
        this.pausedCounter--;
        if (this.pausedCounter > 0)
            return;
        gsap.globalTimeline.play();
        this.audioManager.unpauseAllAudio();
        this.paused = false;
        this.statsInfo.restartAfterPausedGame();
        this.useLastDeltaAnimations = true;
    }
    update = () => {
        requestAnimationFrame(this.update);
        if (this.paused)
            return;
        if (!this.UPDATE_GAME_STEP_BY_STEP)
            this.updateGame();
        this.fpsManager.update(!this.corePhasesManager.isActivePhaseByType(CorePhases.discipline), this.statsInfo.actualFps);
        if (!this.fpsManager.animationsLock30 && !this.fpsManager.animationsPerformanceLock30) {
            const delta = this.clock.getDelta();
            this.updateAnimations(this.useLastDeltaAnimations ? this.lastDeltaAnimations : delta);
            this.useLastDeltaAnimations = false;
            this.lastDeltaAnimations = delta;
        }
        this.renderScene();
        this.rendererInfo.update();
        this.statsInfo.update();
        if (this.DEBUG_ENABLED)
            this.cannonDebugger?.update();
        if (!this.firstUpdateDone && this.readyFirstUpdate) {
            this.firstUpdateDone = true;
            window.dispatchEvent(new CustomEvent(CustomEvents.firstUpdateDone));
        }
    };
    renderScene = () => {
        this.renderManager.renderScene(this.scene, this.cameraManager.getActualRenderCamera());
    };
    updateGame = () => {
        if (this.fpsManager.checkSkip())
            return;
        this.corePhasesManager.update();
        this.timeManager.update(this.paused);
        this.particleManager.update();
        this.dataForCore.callbacks.updateBeforePhysics();
        if (this.physics && !this.PHYSICS_DISABLED && this.physicsEnabled) {
            this.physics.updatePhysics(this.FIXED_DELTA);
        }
        this.dataForCore.callbacks.updateAfterPhysics(this.FIXED_DELTA);
        this.shadowsManager.followObjectWithLight();
        if (this.fpsManager.animationsLock30 || this.fpsManager.animationsPerformanceLock30) {
            this.updateAnimations(this.FIXED_DELTA);
        }
        this.readyFirstUpdate = true;
    };
    updateAnimations(delta) {
        this.dataForCore.callbacks.updateAnimations(delta);
    }
    freeObjectFromMemory = (object) => {
        const mesh = object;
        const material = mesh.material;
        const geometry = mesh.geometry;
        if (material) {
            if (material.map)
                material.map.dispose();
            if (material.lightMap)
                material.lightMap.dispose();
            if (material.aoMap)
                material.aoMap.dispose();
            if (material.emissiveMap)
                material.emissiveMap.dispose();
            if (material.bumpMap)
                material.bumpMap.dispose();
            if (material.normalMap)
                material.normalMap.dispose();
            if (material.displacementMap)
                material.displacementMap.dispose();
            if (material.roughnessMap)
                material.roughnessMap.dispose();
            if (material.metalnessMap)
                material.metalnessMap.dispose();
            if (material.alphaMap)
                material.alphaMap.dispose();
            material.dispose();
        }
        if (geometry) {
            if (geometry.attributes)
                geometry.attributes = {};
            geometry.dispose();
        }
    };
    disposeTexture = (value) => {
        value.dispose();
    };
    disposeAll = () => {
        console.log('DISPOSE ALL!');
        this.texturesToUse?.lightmap.forEach(this.disposeTexture);
        this.texturesToUse?.alphamap.forEach(this.disposeTexture);
        this.texturesToUse?.envmap.forEach(this.disposeTexture);
        this.texturesToUse?.main.forEach(this.disposeTexture);
        this.texturesToUse?.cubemap?.dispose();
        this.materialsToUse?.basic.forEach((value) => {
            value.map?.dispose();
            value.dispose();
        });
        this.renderManager?.renderer?.info.reset();
        this.scene.traverse((object) => {
            this.freeObjectFromMemory(object);
        });
        this.renderManager?.renderer?.dispose();
    };
    setWireframeGeometry() {
        this.scene.traverse((object) => {
            const mesh = object;
            if (mesh.geometry) {
                const wireframe = new THREE.WireframeGeometry(mesh.geometry);
                const line = new THREE.LineSegments(wireframe);
                this.scene.add(line);
            }
        });
    }
    prematureFinishGame(disciplineEndCallback) {
        gsap.globalTimeline.getChildren().forEach(t => t.kill());
        this.audioManager.stopAllAudio();
        disciplineEndCallback();
        this.corePhasesManager.setFinalPhase();
    }
    togglePhysics(value) {
        this.physicsEnabled = value;
    }
    changeUVs(mesh, cloneGeometry = true) {
        if (cloneGeometry)
            mesh.geometry = mesh.geometry.clone();
        const uv = mesh.geometry.attributes.uv;
        const uv2 = mesh.geometry.attributes.uv2;
        mesh.geometry.attributes.uv = uv2;
        mesh.geometry.attributes.uv2 = uv;
    }
    cloneSkeleton(source) {
        const sourceLookup = new Map();
        const cloneLookup = new Map();
        const clone = source.clone();
        this.parallelTraverse(source, clone, (sourceNode, clonedNode) => {
            sourceLookup.set(clonedNode, sourceNode);
            cloneLookup.set(sourceNode, clonedNode);
        });
        clone.traverse((node) => {
            if (!(node instanceof THREE.SkinnedMesh))
                return;
            const clonedMesh = node;
            const sourceMesh = sourceLookup.get(node);
            const sourceBones = sourceMesh.skeleton.bones;
            clonedMesh.skeleton = sourceMesh.skeleton.clone();
            clonedMesh.bindMatrix.copy(sourceMesh.bindMatrix);
            clonedMesh.skeleton.bones = sourceBones.map(function (bone) {
                return cloneLookup.get(bone);
            });
            clonedMesh.bind(clonedMesh.skeleton, clonedMesh.bindMatrix);
        });
        return clone;
    }
    parallelTraverse(a, b, callback) {
        callback(a, b);
        for (let i = 0; i < a.children.length; i++) {
            this.parallelTraverse(a.children[i], b.children[i], callback);
        }
    }
}
//# sourceMappingURL=Game3DManager.js.map